/*
 * Wishlist Drawer Share — overlay layer inside the wishlist drawer panel.
 * Lifecycle (open/close/focus-trap/escape/scroll-lock) is owned by the
 * wishlist drawer. This file owns: submit-pill sync, email form submit via
 * mailto: (opens the visitor's mail client), channel deep-links, clipboard,
 * Native Share, success state (3s auto-dismiss), and the close-button →
 * back-to-wishlist transition.
 * Depends on window.BroraWishlist (for the public share URL).
 */
;(function () {
  'use strict';
  const container = document.querySelector('[data-wishlist-drawer]');
  if (!container) return;
  const layer = container.querySelector('[data-wishlist-drawer-share-layer]');
  if (!layer) return;
  const closeBtn = layer.querySelector('[data-wishlist-drawer-share-close]');
  const announcer = container.querySelector('[data-wishlist-drawer-announcer]');
  const form = layer.querySelector('[data-wishlist-drawer-share-form]');
  const recipientInput = layer.querySelector('[data-wishlist-drawer-share-recipient]');
  const subjectInput = layer.querySelector('[data-wishlist-drawer-share-subject]');
  const messageInput = layer.querySelector('[data-wishlist-drawer-share-message]');
  const submitBtn = layer.querySelector('[data-wishlist-drawer-share-submit]');
  const successEl = layer.querySelector('[data-wishlist-drawer-share-success]');
  const shareSuccessText = successEl ? successEl.textContent.trim() : '';
  let successTimer = null;
  let lastTriggerWithinDrawer = null;
  function announce(t) { if (announcer) announcer.textContent = t || ''; }
  // Visible transient feedback in the share footer — reuses the "Wishlist
  // shared!" slot ([data-wishlist-drawer-share-success]): sets the text, fades
  // it in via the success state, announces it, then clears after 3s.
  function flashMessage(text) {
    if (successEl && text) successEl.textContent = text;
    container.setAttribute('data-wishlist-drawer-share-state', 'success');
    announce(text || '');
    if (successTimer) clearTimeout(successTimer);
    successTimer = setTimeout(function () {
      container.removeAttribute('data-wishlist-drawer-share-state');
      successTimer = null;
    }, 3000);
  }
  function tabs() { return container.querySelectorAll('[data-mini-bag-tab]'); }
  // Public wishlist URL via the BroraWishlist seam. Without a list id the page shows the reader
  // their own list, which beats a dead link.
  function buildShareUrl() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const fallback = window.location.origin + root + 'pages/wishlist';
    if (!window.BroraWishlist || typeof window.BroraWishlist.getShareUrl !== 'function') return Promise.resolve(fallback);
    return window.BroraWishlist.getShareUrl().then(function (url) {
      return url || fallback;
    }).catch(function () { return fallback; });
  }

  function syncSubmitPill() {
    if (!form || !submitBtn) return;
    const isValid = form.checkValidity();
    submitBtn.toggleAttribute('aria-disabled', !isValid);
    submitBtn.toggleAttribute('disabled', !isValid);
  }

  function resetForm() {
    if (!form) return;
    form.reset();
    form.querySelectorAll('[aria-invalid="true"]').forEach(function (f) { f.removeAttribute('aria-invalid'); });
    form.querySelectorAll('[data-error-message]').forEach(function (el) { el.textContent = ''; });
  }

  function openShare(triggerEl) {
    lastTriggerWithinDrawer = triggerEl || null;
    layer.removeAttribute('aria-hidden'); layer.removeAttribute('inert');
    tabs().forEach(function (t) { t.setAttribute('inert', ''); });
    requestAnimationFrame(function () {
      container.setAttribute('data-wishlist-drawer-share', 'open');
      // The layer is still off-screen when focus lands; scrolling it into view would shift the panel by
      // the exact distance the layer is travelling, leaving the entry animation invisible.
      requestAnimationFrame(function () { if (recipientInput && typeof recipientInput.focus === 'function') recipientInput.focus({ preventScroll: true }); });
    });
  }

  function closeShare() {
    container.setAttribute('data-wishlist-drawer-share', 'closed');
    container.removeAttribute('data-wishlist-drawer-share-state');
    if (successTimer) { clearTimeout(successTimer); successTimer = null; }
    resetForm(); syncSubmitPill(); announce('');
    tabs().forEach(function (t) { t.removeAttribute('inert'); });
    layer.setAttribute('aria-hidden', 'true'); layer.setAttribute('inert', '');
    if (lastTriggerWithinDrawer && typeof lastTriggerWithinDrawer.focus === 'function' && document.contains(lastTriggerWithinDrawer)) lastTriggerWithinDrawer.focus();
    lastTriggerWithinDrawer = null;
  }

  // Email share is client-side via mailto: (opens the visitor's mail client).
  // No backend / no third-party key — sending branded email from the system
  // (e.g. to the customer themselves) is a CRM job (Voyado Engage) for later.
  function buildMailto(recipient, subject, body) {
    const q = [];
    if (subject) q.push('subject=' + encodeURIComponent(subject));
    if (body) q.push('body=' + encodeURIComponent(body));
    return 'mailto:' + encodeURIComponent(recipient).replace(/%40/g, '@') + (q.length ? '?' + q.join('&') : '');
  }

  function onFormValid(e) {
    e.preventDefault();
    buildShareUrl().then(function (url) {
      const recipient = (recipientInput && recipientInput.value || '').trim();
      const subject = subjectInput && subjectInput.value || '';
      const message = messageInput && messageInput.value || '';
      const body = (message ? message + '\n\n' : '') + url;
      window.location.href = buildMailto(recipient, subject, body);
      flashMessage(shareSuccessText);
    });
  }

  // Subject + message from the share form, used as lead text for the channels
  // that support it (WhatsApp, X). Empty when the visitor hasn't filled them —
  // those channels then fall back to URL-only.
  function shareLead() {
    const s = (subjectInput && subjectInput.value || '').trim();
    const m = (messageInput && messageInput.value || '').trim();
    return [s, m].filter(Boolean).join('\n');
  }

  function deepLink(channel, url) {
    const lead = shareLead();
    // WhatsApp: single text blob — lead + URL together.
    if (channel === 'whatsapp') return 'https://wa.me/?text=' + encodeURIComponent(lead ? lead + '\n\n' + url : url);
    // Facebook: sharer.php takes only the URL (custom text unsupported; preview
    // comes from the shared page's Open Graph tags).
    if (channel === 'facebook') return 'https://www.facebook.com/sharer/sharer.php?u=' + encodeURIComponent(url);
    // X: text = post body; X appends the (t.co-shortened, ~23 char) url param.
    // Trim the lead so text + url stay within the 280-char limit.
    if (channel === 'x') {
      const text = lead.length > 240 ? lead.slice(0, 239) + '…' : lead;
      return 'https://twitter.com/intent/tweet?' + (text ? 'text=' + encodeURIComponent(text) + '&' : '') + 'url=' + encodeURIComponent(url);
    }
    return null;
  }

  function copyLink(url, btn) {
    const copied = btn && btn.getAttribute('data-copied-msg') || '';
    const errored = btn && btn.getAttribute('data-error-msg') || '';
    if (!navigator.clipboard || !navigator.clipboard.writeText) { flashMessage(errored); return; }
    navigator.clipboard.writeText(url).then(function () { flashMessage(copied); })
      .catch(function () { flashMessage(errored); });
  }

  // Instagram/TikTok have no web share URL (unlike WhatsApp/X), so there is no
  // way to open them directly: navigator.share() only opens the OS picker (the
  // user chooses the target — we can't pre-select or detect available targets,
  // canShare() only validates the data shape). We keep navigator.share on every
  // device (incl. desktop) because the picker DOES surface real targets there
  // (e.g. Messages/Instagram on macOS); where it's unavailable we fall back to
  // copy + a "paste it on …" hint. Intentionally NOT gated to touch-only.
  function nativeShareOrCopy(channel, url, btn) {
    if (navigator.share) {
      navigator.share({ title: btn && btn.getAttribute('data-native-label') || '', url: url }).catch(function (err) {
        if (err && err.name === 'AbortError') return;
        copyLink(url, btn);
        announce(btn && btn.getAttribute('data-paste-hint') || '');
      });
      return;
    }
    copyLink(url, btn);
    announce(btn && btn.getAttribute('data-paste-hint') || '');
  }

  function onChannelClick(e) {
    const btn = e.target.closest('[data-wishlist-drawer-share-channel]');
    if (!btn || !layer.contains(btn)) return;
    const channel = btn.getAttribute('data-wishlist-drawer-share-channel');
    buildShareUrl().then(function (url) {
      if (channel === 'copy') return copyLink(url, btn);
      const link = deepLink(channel, url);
      if (link) { window.open(link, '_blank', 'noopener,noreferrer'); return; }
      if (channel === 'instagram' || channel === 'tiktok') nativeShareOrCopy(channel, url, btn);
    });
  }

  if (closeBtn) closeBtn.addEventListener('click', closeShare);
  if (form) { form.addEventListener('form:valid', onFormValid); form.addEventListener('input', syncSubmitPill); }
  layer.addEventListener('click', onChannelClick);
  syncSubmitPill();

  window.BroraWishlistDrawerShare = { open: openShare, close: closeShare, syncSubmitPill: syncSubmitPill };
})();
